import { useState } from "react";
import type { ProductionReferenceData, TerminalAdministration } from "../models";
import { productionGateway } from "../production-gateway";
import { today } from "../production-time";
import { useTerminalPairingWorkspace } from "./useTerminalPairingWorkspace";
import type { ProductionWorkspaceProps } from "../production-workspace-contract";

export type MachineAssignmentOptions = {
  data: TerminalAdministration;
  reference: ProductionReferenceData;
  shiftCode: string;
  onSaved: () => Promise<void>;
  notify: ProductionWorkspaceProps["notify"];
};

export function useMachineAssignmentWorkspace({ data, reference, shiftCode, onSaved, notify }: MachineAssignmentOptions) {
  const [lineId, setLineId] = useState(reference.lines[0]?.id ?? "");
  const [operatorId, setOperatorId] = useState(reference.operators[0]?.id ?? "");
  const [assignmentRole, setAssignmentRole] = useState<"PRIMARY" | "ASSISTANT">("PRIMARY");
  const [assignmentShiftCode, setAssignmentShiftCode] = useState(shiftCode);
  const [repeatDaily, setRepeatDaily] = useState(true);
  const [allowConcurrentAssignment, setAllowConcurrentAssignment] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<TerminalAdministration["assignments"][number] | null>(null);
  const [recurringCancelTargetId, setRecurringCancelTargetId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const pairing = useTerminalPairingWorkspace({ data, reference, onSaved, notify, setSaving });
  const [workspaceView, setWorkspaceView] = useState<"shift" | "assign" | "technical" | "security" | "audit">("shift");
  const [securityView, setSecurityView] = useState<"pin" | "terminal">("pin");
  const activeAssignments = data.assignments.filter((item) => item.shiftDate === today() && item.shiftCode === shiftCode && item.status === "ACTIVE");
  const concurrentLineIds = new Set([
    ...data.assignments.filter((item) => item.shiftDate === today() && item.shiftCode === assignmentShiftCode && item.status === "ACTIVE" && item.operatorId === operatorId && item.lineId !== lineId).map((item) => item.lineId),
    ...(data.recurringAssignments ?? []).filter((item) => item.shiftCode === assignmentShiftCode && item.status === "ACTIVE" && item.operatorId === operatorId && item.lineId !== lineId).map((item) => item.lineId),
  ]);
  const selectedLine = reference.lines.find((item) => item.id === lineId);
  const selectedOperator = reference.operators.find((item) => item.id === operatorId);
  const concurrentLineNames = [...concurrentLineIds].map((id) => reference.lines.find((line) => line.id === id)?.name ?? "Başka makine");
  const save = async () => {
    const operator = reference.operators.find((item) => item.id === operatorId);
    if (!lineId || !operator) return;
    setSaving(true);
    try {
      await productionGateway.assignOperator({ lineId, shiftDate: today(), shiftCode: assignmentShiftCode, operatorId: operator.id, operatorName: operator.name, assignmentRole, allowConcurrentAssignment, repeatDaily });
      setAllowConcurrentAssignment(false);
      notify(repeatDaily ? `${assignmentShiftCode} için kalıcı operatör düzeni kaydedildi. Girişte operatör PIN’i gerekir.` : `${assignmentShiftCode} için yalnız bugüne ait operatör ataması kaydedildi.`); await onSaved();
    } catch (error) { notify(error instanceof Error ? error.message : "Atama kaydedilemedi."); }
    finally { setSaving(false); }
  };
  const cancel = async () => {
    if (!cancelTarget) return;
    setSaving(true);
    try {
      await productionGateway.cancelAssignment(cancelTarget.id);
      setCancelTarget(null);
      notify(cancelTarget.repeatDaily ? "Kalıcı görevlendirme kaldırıldı; sonraki günlerde tekrarlanmayacak. Varsa bağlı açık oturum kapatıldı." : "Operatör ataması iptal edildi; varsa açık makine oturumu güvenli biçimde kapatıldı."); await onSaved();
    } catch (error) { notify(error instanceof Error ? error.message : "Atama iptal edilemedi."); }
    finally { setSaving(false); }
  };
  const cancelRecurring = async () => {
    if (!recurringCancelTargetId) return;
    setSaving(true);
    try {
      await productionGateway.cancelRecurringAssignment(recurringCancelTargetId);
      setRecurringCancelTargetId(null);
      notify("Kalıcı görevlendirme kaldırıldı. Geçmiş kayıtlar korundu; varsa bağlı açık oturum kapatıldı.");
      await onSaved();
    } catch (error) { notify(error instanceof Error ? error.message : "Kalıcı görevlendirme kaldırılamadı."); }
    finally { setSaving(false); }
  };
  return { lineId, setLineId, operatorId, setOperatorId, assignmentRole, setAssignmentRole, assignmentShiftCode, setAssignmentShiftCode, repeatDaily, setRepeatDaily, allowConcurrentAssignment, setAllowConcurrentAssignment, cancelTarget, setCancelTarget, recurringCancelTargetId, setRecurringCancelTargetId, saving, workspaceView, setWorkspaceView, securityView, setSecurityView, activeAssignments, concurrentLineIds, selectedLine, selectedOperator, concurrentLineNames, save, cancel, cancelRecurring, pairing };
}
